import { useTranslation } from 'react-i18next';
import { Head } from '~/components/shared/Head';
import { useParams, useRoutes, RouteObject, Outlet } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { CustomerSearch, type Customer } from './components/CustomerSearch';
import CustomerTable from './components/CustomerTable';
import CustomerDetail from './components/CustomerDetail';
import CustomerProfile from './components/CustomerProfile';
import CustomerDocuments from './components/CustomerDocuments';
import CustomerAttributes from './components/CustomerAttributes';

const Customer = () => {
  const [t] = useTranslation('customer');
  const { agentId } = useParams();
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearchResults = useCallback((customers: Customer[], searchActive: boolean) => {
    setSearchResults(customers);
    setIsSearchActive(searchActive);
  }, []);

  const handleClearResults = useCallback(() => {
    setSearchResults([]);
    setIsSearchActive(false);
  }, []);

  const handleLoadingChange = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const routes: RouteObject[] = [
    {
      index: true,
      element: (
        <>
          {/* Header Section */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold mb-2">{t('title')}</h1>
            <p className="text-base-content/70 mb-6">{t('description')}</p>
          </div>

          {/* Search Section */}
          <div className="mb-6">
            {agentId && (
              <CustomerSearch
                agentId={agentId}
                onSearchResults={handleSearchResults}
                onClearResults={handleClearResults}
                onLoadingChange={handleLoadingChange}
              />
            )}
          </div>

          <div className="mb-24 w-full lg:mb-0">
            <CustomerTable
              customers={searchResults}
              loading={isLoading}
            />
          </div>
        </>
      ),
    },
    {
      path: ':customerId',
      element: <CustomerDetail />,
      children: [
        {
          index: true,
          element: <CustomerProfile />,
        },
        {
          path: 'attributes',
          element: <CustomerAttributes />,
        },
        {
          path: 'media',
          element: <CustomerDocuments />,
        },
      ],
    },
  ];

  const element = useRoutes(routes);

  return (
    <div className="m-auto w-full max-w-[1200px] px-8 pt-12">
      {element}
    </div>
  );
};

export default Customer;
